import { type MouseEvent, useEffect, useState } from 'react';
import { Flex, Paper, Radio, Stack, Switch, Text } from '@mantine/core';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import {
  automaticNoteTemplate,
  firmDefaultTemplate,
} from 'src/data-capture/hooks/templates/defaultTemplate';
import useTemplates, {
  type TemplateWithoutSections,
} from 'src/data-capture/hooks/templates/useTemplates';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SelectableList } from 'src/UI/generate-flow/SelectableList';
import { AdditionalInstructionsField } from '../components/AdditionalInstructionsField';
import { AutomaticNotesCard } from '../components/default-template/AutomaticNotesCard';
import { DefaultTemplateBadge } from '../components/default-template/DefaultTemplateBadge';
import { MakeDefaultAction } from '../components/default-template/MakeDefaultAction';
import { MakeDefaultTemplateModal } from '../components/default-template/MakeDefaultTemplateModal';
import { MeetingNoteStepProps } from '../types';

export function NoteTemplateStep({ data, setData }: MeetingNoteStepProps) {
  const { files: templates, isLoading } = useTemplates({ templateType: 'MEETING_NOTE' });
  const { flagEnabledDefaultTemplates } = useFlags();
  const [templateToMakeDefault, setTemplateToMakeDefault] = useState<TemplateWithoutSections>();

  useEffect(() => {
    if (!data.template && templates.length > 0) {
      const defaultTemplate = flagEnabledDefaultTemplates
        ? firmDefaultTemplate(templates)
        : templates.find(t => t.isDefault);
      if (defaultTemplate) {
        setData(prev => ({ ...prev, template: defaultTemplate }));
      }
    }
  }, [templates, data.template, setData, flagEnabledDefaultTemplates]);

  if (isLoading) return null;

  const defaultTemplate = flagEnabledDefaultTemplates ? firmDefaultTemplate(templates) : undefined;
  const automaticTemplate = flagEnabledDefaultTemplates
    ? automaticNoteTemplate(templates)
    : undefined;

  const selectTemplate = (template: TemplateWithoutSections) =>
    setData(prev => ({ ...prev, template }));

  const handleRowClick = (event: MouseEvent<HTMLDivElement>, template: TemplateWithoutSections) => {
    if (event.defaultPrevented) return;
    selectTemplate(template);
  };

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard
        title="Select a template for the meeting note"
        subTitle={
          defaultTemplate && data.template?.id === defaultTemplate.id
            ? "We've picked your firm's default."
            : undefined
        }
      >
        <Flex direction="column" gap="md">
          <AutomaticNotesCard />
          <SelectableList
            items={templates}
            getKey={t => t.id}
            renderRow={template => (
              // Not a button: the "Make default" control sits inside the row, and a button can't hold
              // another. The radio is what keyboard and screen reader users select with.
              <Paper
                radius="12"
                withBorder
                px="md"
                py="sm"
                data-make-default-row
                onClick={(event: MouseEvent<HTMLDivElement>) => handleRowClick(event, template)}
                styles={theme => ({
                  root: {
                    cursor: 'pointer',
                    borderColor:
                      data.template?.id === template.id
                        ? theme.colors.brand[6]
                        : 'var(--default-border-color)',
                    boxShadow: 'none',
                    userSelect: 'none',
                  },
                })}
              >
                <Flex align="center" gap="md">
                  <Radio
                    name="meeting-note-template"
                    aria-label={template.displayName}
                    checked={data.template?.id === template.id}
                    onChange={() => selectTemplate(template)}
                    color="brand"
                    size="md"
                    iconColor="white"
                  />
                  <Stack gap={2} flex={1} miw={0}>
                    <Flex align="center" gap="xs">
                      <Text fw={FONT_WEIGHTS.semibold} size="sm" c="gray.8">
                        {template.displayName}
                      </Text>
                      {template.id === defaultTemplate?.id && <DefaultTemplateBadge />}
                    </Flex>
                    {template.id === automaticTemplate?.id && (
                      <Text size="xs" c="gray.6">
                        Used for automatic notes
                      </Text>
                    )}
                  </Stack>
                  {flagEnabledDefaultTemplates &&
                    template.isActive &&
                    template.id !== defaultTemplate?.id && (
                      <MakeDefaultAction onClick={() => setTemplateToMakeDefault(template)} />
                    )}
                </Flex>
              </Paper>
            )}
          />
          <Flex justify="space-between" align="center" px="xs">
            <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
              Summarise meeting note
            </Text>
            <Switch
              checked={data.summarise}
              onChange={event =>
                setData(prev => ({ ...prev, summarise: event.currentTarget.checked }))
              }
            />
          </Flex>
          <AdditionalInstructionsField
            templateId={data.template?.id}
            displayName={data.template?.displayName}
            isAutomatic={!!data.template && data.template.id === automaticTemplate?.id}
            eligibleForSummarisation={data.template?.eligibleForSummarisation ?? true}
            value={data.additionalInstructions ?? ''}
            onChange={value => setData(prev => ({ ...prev, additionalInstructions: value }))}
          />
        </Flex>
      </StepCard>
      {flagEnabledDefaultTemplates && (
        <MakeDefaultTemplateModal
          template={templateToMakeDefault}
          onClose={() => setTemplateToMakeDefault(undefined)}
        />
      )}
    </Flex>
  );
}
